/**
 * Session.tsx — the core WebRTC viewer (SPEC §4 + §9 + §10).
 *
 * The browser is the ANSWERER: it POSTs /sessions, opens the signaling WS, and
 * answers the agent's offer. The inbound H.264 media track is attached to
 * <video>; mouse/keyboard are captured and sent as SPEC §9.1 events on the
 * `input` channel (HID-usage mapped). Features: view-only toggle, multi-monitor
 * switch, connection stats, consent badge, panic-disconnect.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useApi } from '@/api/provider';
import { startSession, type ActiveSession } from '@/lib/session';
import { InputController } from '@/lib/input';
import { useToast } from '@/components/Toast';
import { sealData } from '@/lib/e2ee';
import { toArrayBuffer } from '@/lib/binary';
import {
  CHANNELS,
  type ConnStats,
  type ControlMessage,
  type MonitorInfo,
  type MonitorList,
  type SessionMode,
} from '@/types';

type SessionStatus = 'connecting' | 'connected' | 'secure' | 'reconnecting' | 'ended' | 'error';

export default function Session() {
  const { deviceId = '' } = useParams();
  const [params] = useSearchParams();
  const mode = (params.get('mode') as SessionMode) || 'control';
  const api = useApi();
  const navigate = useNavigate();
  const toast = useToast();

  const videoRef = useRef<HTMLVideoElement>(null);
  const sessionRef = useRef<ActiveSession | null>(null);
  const inputRef = useRef<InputController | null>(null);

  const [status, setStatus] = useState<SessionStatus>('connecting');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [stats, setStats] = useState<ConnStats | null>(null);
  const [monitors, setMonitors] = useState<MonitorInfo[]>([]);
  const [monitor, setMonitor] = useState(0);
  const [viewOnly, setViewOnly] = useState(mode === 'view');
  const [machineName, setMachineName] = useState<string>('Remote session');
  const [isFullscreen, setIsFullscreen] = useState(
    typeof document !== 'undefined' && !!document.fullscreenElement,
  );

  // Refs read inside the InputController closures (avoid stale captures).
  const monitorRef = useRef(monitor);
  const viewOnlyRef = useRef(viewOnly);
  monitorRef.current = monitor;
  viewOnlyRef.current = viewOnly;

  const onControl = useCallback((msg: ControlMessage) => {
    switch (msg.kind) {
      case 'monitors': {
        const list = msg.data as MonitorList | undefined;
        if (list?.monitors) {
          setMonitors(list.monitors);
          if (typeof list.active === 'number') setMonitor(list.active);
        }
        break;
      }
      default:
        break;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const session = await startSession(api, deviceId, mode, {
          onTrack: (stream) => {
            if (videoRef.current) videoRef.current.srcObject = stream;
            setStatus('connected');
          },
          onChannel: (label, ch) => {
            if (label === CHANNELS.input) {
              // Bind input capture once the input channel is open.
              const surface = videoRef.current;
              if (!surface) return;
              const ctrl = new InputController({
                channel: ch,
                surface,
                monitor: () => monitorRef.current,
                viewOnly: () => viewOnlyRef.current,
                // Pointer Lock only in control mode (relative mouse, edge reach).
                pointerLock: mode === 'control',
                // E2EE-seal input payloads once session keys exist (SPEC §10).
                sealFn: (json) => {
                  const keys = sessionRef.current?.sessionKeys() ?? null;
                  // Drop input until E2EE is established — never send it cleartext.
                  if (!keys) return null;
                  return toArrayBuffer(sealData(keys, new TextEncoder().encode(json)));
                },
              });
              ctrl.attach();
              inputRef.current = ctrl;
            }
          },
          onControl,
          onStats: setStats,
          onSecure: () => setStatus('secure'),
          onConnState: (s) => {
            if (cancelled) return;
            if (s === 'reconnecting') {
              setStatus('reconnecting');
              toast.warn('Connection dropped — reconnecting…', { ttlMs: 4000 });
            } else if (s === 'connected') {
              setStatus((cur) => (cur === 'secure' ? cur : 'connected'));
            } else if (s === 'failed') {
              setStatus('error');
              setErrorMsg('Connection failed.');
              toast.error('Connection failed.');
            }
          },
          onError: (m) => {
            if (cancelled) return;
            setErrorMsg(m);
            setStatus('error');
            toast.error(m);
          },
          onEnded: () => {
            if (cancelled) return;
            setStatus('ended');
          },
        });
        if (cancelled) {
          session.end();
          return;
        }
        sessionRef.current = session;
        // Nudge the agent for a fresh keyframe up front; the monitor list arrives
        // over `control` once the channel opens (agent sendMonitors on open).
        session.sendControl({ kind: 'keyframe' });
      } catch (err) {
        if (!cancelled) {
          const m = err instanceof Error ? err.message : 'Failed to start session.';
          setErrorMsg(m);
          setStatus('error');
          toast.error(m);
        }
      }
    })();

    return () => {
      cancelled = true;
      inputRef.current?.detach();
      sessionRef.current?.end();
      sessionRef.current = null;
    };
    // `toast` is a stable memoized API; including it would needlessly re-establish
    // the session on unrelated re-renders. The session is bound once per
    // (api, deviceId, mode, onControl).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, deviceId, mode, onControl]);

  // Keyboard Lock API (Chromium) — captures Ctrl+W / Alt+Tab / Meta while in a
  // control session so chords reach the remote (architecture §4.5).
  useEffect(() => {
    if (viewOnly) return;
    const kb = (navigator as Navigator & { keyboard?: { lock?: (keys?: string[]) => Promise<void>; unlock?: () => void } }).keyboard;
    kb?.lock?.().catch(() => {});
    return () => kb?.unlock?.();
  }, [viewOnly]);

  // Best-effort machine name for the action-bar title; falls back to the default
  // 'Remote session' if the lookup fails. Uses the existing api client only.
  useEffect(() => {
    let cancelled = false;
    if (!deviceId) return;
    api
      .getDevice(deviceId)
      .then((d) => {
        if (!cancelled && d?.name) setMachineName(d.name);
      })
      .catch(() => {
        /* non-fatal: keep the default label */
      });
    return () => {
      cancelled = true;
    };
  }, [api, deviceId]);

  // Track fullscreen state so the toolbar button reflects the real document mode
  // (the user can also exit via Esc / browser chrome).
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  function toggleFullscreen() {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void document.documentElement.requestFullscreen().catch(() => {});
    }
  }

  function toggleViewOnly() {
    const next = !viewOnly;
    setViewOnly(next);
    inputRef.current?.setViewOnly(next);
  }

  function switchMonitor(id: number) {
    setMonitor(id);
    sessionRef.current?.sendControl({ kind: 'set_monitor', data: { monitor: id } });
  }

  function disconnect() {
    inputRef.current?.detach();
    sessionRef.current?.end();
    navigate('/devices');
  }

  const statusLabel = useMemo(() => {
    switch (status) {
      case 'connecting':
        return 'Connecting…';
      case 'connected':
        return 'Connected (negotiating E2EE)';
      case 'secure':
        return 'Secure';
      case 'reconnecting':
        return 'Reconnecting…';
      case 'ended':
        return 'Session ended';
      case 'error':
        return 'Error';
    }
  }, [status]);

  return (
    <div className="flex h-screen flex-col bg-black">
      {/* Action bar — professional remote-control toolbar. LEFT: identity +
          security badges; RIGHT: grouped icon actions wired to existing handlers. */}
      <div className="flex items-center gap-3 border-b border-helm-border bg-helm-panel px-4 py-2 shadow-soft">
        {/* ---- LEFT: machine identity + status + security ---- */}
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="flex items-center gap-2 truncate text-sm font-semibold text-helm-text" title={machineName}>
            <MonitorIcon />
            <span className="truncate">{machineName}</span>
          </span>

          <span className="h-5 w-px bg-helm-border" aria-hidden />

          <span
            className={`rh-badge ${
              status === 'secure'
                ? 'bg-helm-good/15 text-helm-good'
                : status === 'error' || status === 'ended'
                  ? 'bg-helm-bad/15 text-helm-bad'
                  : 'bg-helm-warn/15 text-helm-warn'
            }`}
          >
            <span
              className={`h-1.5 w-1.5 rounded-full ${
                status === 'secure'
                  ? 'bg-helm-good'
                  : status === 'reconnecting'
                    ? 'bg-helm-warn animate-pulse'
                    : status === 'error' || status === 'ended'
                      ? 'bg-helm-bad'
                      : 'bg-helm-warn animate-pulse'
              }`}
              aria-hidden
            />
            {statusLabel}
          </span>

          {/* E2EE lock indicator (SPEC §10): blind relay only once secure. */}
          <span
            className={`rh-badge inline-flex items-center gap-1 ${status === 'secure' ? 'bg-helm-good/15 text-helm-good' : 'bg-helm-elevated text-helm-muted'}`}
            title={status === 'secure' ? 'End-to-end encrypted (ChaCha20-Poly1305)' : 'E2EE handshake pending'}
          >
            <LockIcon open={status !== 'secure'} />
            {status === 'secure' ? 'E2EE' : 'negotiating'}
          </span>

        </div>

        {/* ---- RIGHT: grouped action buttons ---- */}
        <div className="ml-auto flex items-center gap-1.5">
          {/* Group: input mode + monitor */}
          <div className="flex items-center gap-1.5">
            {/* View / Control two-state toggle (existing toggleViewOnly). */}
            <div className="inline-flex overflow-hidden rounded-lg border border-helm-border bg-helm-elevated/40 text-xs font-medium">
              <button
                type="button"
                onClick={() => viewOnly && toggleViewOnly()}
                disabled={mode === 'view'}
                aria-pressed={!viewOnly}
                title={mode === 'view' ? 'This session is view-only' : 'Take control — send mouse & keyboard'}
                className={`flex items-center gap-1 px-2.5 py-1 transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                  !viewOnly ? 'bg-helm-accent text-white' : 'text-helm-muted hover:text-helm-text'
                }`}
              >
                <CursorIcon />
                Control
              </button>
              <button
                type="button"
                onClick={() => !viewOnly && toggleViewOnly()}
                aria-pressed={viewOnly}
                title="View only — do not send input to the remote machine"
                className={`flex items-center gap-1 px-2.5 py-1 transition-colors ${
                  viewOnly ? 'bg-helm-warn/20 text-helm-warn' : 'text-helm-muted hover:text-helm-text'
                }`}
              >
                <EyeIcon />
                View
              </button>
            </div>

            {monitors.length > 1 && (
              <select
                value={monitor}
                onChange={(e) => switchMonitor(Number(e.target.value))}
                title="Switch monitor"
                className="rh-input w-auto py-1 text-xs"
              >
                {monitors.map((m) => (
                  <option key={m.id} value={m.id}>
                    Monitor {m.id + 1} ({m.width}×{m.height}){m.primary ? ' • primary' : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          <Divider />

          {/* Group: screen */}
          <IconButton
            title="Refresh screen (request keyframe)"
            onClick={() => sessionRef.current?.sendControl({ kind: 'keyframe' })}
          >
            <RefreshIcon />
          </IconButton>
          <IconButton
            title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            onClick={toggleFullscreen}
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>

          <Divider />

          {/* Group: tools */}
          <IconButton
            title="Terminal"
            onClick={() => sessionRef.current?.channel(CHANNELS.shell) && navigate(`/session/${deviceId}/shell`)}
          >
            <TerminalIcon />
          </IconButton>
          <IconButton
            title="File transfer"
            onClick={() => navigate(`/session/${deviceId}/files`)}
          >
            <FilesIcon />
          </IconButton>

          <Divider />

          <StatsPill stats={stats} />

          <Divider />

          {/* End session — danger action (existing disconnect). */}
          <IconButton title="End session" onClick={disconnect} danger>
            <EndCallIcon />
          </IconButton>
        </div>
      </div>

      {/* Viewer surface */}
      <div className="relative flex flex-1 items-center justify-center overflow-hidden">
        {/* Prominent view-only indicator (top-center) — never trusted as the only
            gate; the agent + broker also enforce (SPEC §9.1/§11.3). */}
        {viewOnly && (
          <div className="pointer-events-none absolute top-3 left-1/2 z-10 -translate-x-1/2 rounded-full border border-helm-warn/50 bg-helm-warn/15 px-3 py-1 text-xs font-semibold text-helm-warn">
            VIEW ONLY — input is not sent to the remote machine
          </div>
        )}

        {/* Reconnect overlay dims the surface while the transport recovers. */}
        {status === 'reconnecting' && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/60">
            <div className="flex items-center gap-2 rounded-md border border-helm-warn/40 bg-helm-panel px-4 py-2 text-sm text-helm-warn">
              <span className="h-2 w-2 animate-pulse rounded-full bg-helm-warn" aria-hidden />
              Reconnecting…
            </div>
          </div>
        )}

        {errorMsg && (
          <div role="alert" className="absolute z-30 rounded-md border border-helm-bad/40 bg-helm-bad/10 px-4 py-2 text-sm text-helm-bad">
            {errorMsg}
          </div>
        )}

        {/* Click the surface to engage Pointer Lock in control mode. */}
        {status !== 'reconnecting' && mode === 'control' && !viewOnly && (
          <div className="pointer-events-none absolute bottom-3 left-1/2 z-10 -translate-x-1/2 rounded-full bg-black/50 px-3 py-1 text-[11px] text-helm-muted">
            Click to capture mouse · Esc to release
          </div>
        )}

        {/* tabIndex makes the <video> focusable so it receives key events. */}
        <video
          ref={videoRef}
          tabIndex={0}
          autoPlay
          playsInline
          muted
          className="max-h-full max-w-full bg-black outline-none"
          style={{ cursor: viewOnly ? 'default' : 'none' }}
        />
      </div>
    </div>
  );
}

function StatsPill({ stats }: { stats: ConnStats | null }) {
  if (!stats) return <span className="text-xs text-helm-muted">—</span>;
  return (
    <span className="flex items-center gap-2 rounded-lg border border-helm-border bg-helm-elevated/40 px-2.5 py-1 text-xs font-medium text-helm-muted">
      <span title="Transport">{stats.candidatePair === 'relay' ? 'relay' : 'p2p'}</span>
      {stats.frameWidth ? <span>{stats.frameWidth}×{stats.frameHeight}</span> : null}
      {stats.fps ? <span>{Math.round(stats.fps)}fps</span> : null}
      {stats.bitrateKbps ? <span>{stats.bitrateKbps}kbps</span> : null}
      {stats.rttMs !== undefined ? <span>{stats.rttMs}ms</span> : null}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Action-bar primitives
// ---------------------------------------------------------------------------
function Divider() {
  return <span className="mx-0.5 h-6 w-px bg-helm-border" aria-hidden />;
}

function IconButton({
  title,
  onClick,
  disabled,
  danger,
  children,
}: {
  title: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      className={`flex h-8 w-8 items-center justify-center rounded-lg border transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
        danger
          ? 'border-helm-bad/30 text-helm-bad hover:bg-helm-bad/15'
          : 'border-helm-border bg-helm-elevated/40 text-helm-muted hover:bg-helm-elevated hover:text-helm-text'
      }`}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Inline stroke icons (16–18px, stroke=currentColor). No external assets.
// ---------------------------------------------------------------------------
const SVG_PROPS = {
  width: 17,
  height: 17,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function MonitorIcon() {
  return (
    <svg {...SVG_PROPS} aria-hidden>
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </svg>
  );
}

function LockIcon({ open }: { open: boolean }) {
  return (
    <svg {...SVG_PROPS} width={13} height={13} aria-hidden>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      {open ? <path d="M8 11V7a4 4 0 0 1 7.5-2" /> : <path d="M8 11V7a4 4 0 0 1 8 0v4" />}
      <path d="M12 15v2" />
    </svg>
  );
}

function CursorIcon() {
  return (
    <svg {...SVG_PROPS} width={14} height={14} aria-hidden>
      <path d="M5 3l6 16 2-7 7-2L5 3z" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg {...SVG_PROPS} width={14} height={14} aria-hidden>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg {...SVG_PROPS} aria-hidden>
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <path d="M21 3v6h-6" />
    </svg>
  );
}

function FullscreenIcon() {
  return (
    <svg {...SVG_PROPS} aria-hidden>
      <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M16 21h3a2 2 0 0 0 2-2v-3M8 21H5a2 2 0 0 1-2-2v-3" />
    </svg>
  );
}

function FullscreenExitIcon() {
  return (
    <svg {...SVG_PROPS} aria-hidden>
      <path d="M3 8h3a2 2 0 0 0 2-2V3M21 8h-3a2 2 0 0 1-2-2V3M21 16h-3a2 2 0 0 0-2 2v3M3 16h3a2 2 0 0 1 2 2v3" />
    </svg>
  );
}

function TerminalIcon() {
  return (
    <svg {...SVG_PROPS} aria-hidden>
      <path d="M4 17l6-6-6-6M12 19h8" />
    </svg>
  );
}

function FilesIcon() {
  return (
    <svg {...SVG_PROPS} aria-hidden>
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
    </svg>
  );
}

function EndCallIcon() {
  return (
    <svg {...SVG_PROPS} aria-hidden>
      <path d="M2 9.5c5-3.5 15-3.5 20 0 0 0 .5 2-1 3l-3-1c-1-.5-1-1.5-1-2.5-3-1.5-7-1.5-10 0 0 1 0 2-1 2.5l-3 1c-1.5-1-1-3-1-3z" />
    </svg>
  );
}
